import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { TIER_LIMITS, UserTier } from '@/lib/tier-utils';

interface Goal {
  id: string;
  title: string;
}

interface Habit {
  id: string;
  title: string;
  goalTitle?: string;
}

interface DowngradeCheckResult {
  needsDowngradeSelection: boolean;
  allGoals: Goal[];
  allHabits: Habit[];
  loading: boolean;
  refreshCheck: () => void;
}

/**
 * Check if a free tier user has excess goals/habits that need to be archived
 */
export function useDowngradeCheck(): DowngradeCheckResult {
  const { user } = useAuth();
  const { profile } = useProfile();
  const [needsDowngradeSelection, setNeedsDowngradeSelection] = useState(false);
  const [allGoals, setAllGoals] = useState<Goal[]>([]);
  const [allHabits, setAllHabits] = useState<Habit[]>([]);
  const [loading, setLoading] = useState(true);

  const checkDowngrade = useCallback(async () => {
    if (!user || !profile) {
      setLoading(false);
      return;
    }

    try {
      let currentTier: UserTier = (profile.tier as UserTier) || 'free';

      // Check if beta_pro has expired
      if (currentTier === 'beta_pro' && profile.beta_pro_expires_at) {
        const expiresAt = new Date(profile.beta_pro_expires_at);
        if (expiresAt < new Date()) {
          currentTier = 'free';
        }
      }

      // Only check if user is on free tier
      if (currentTier !== 'free') {
        setNeedsDowngradeSelection(false);
        setLoading(false);
        return;
      }

      const limits = TIER_LIMITS.free;

      // Fetch active (non-archived) goals
      const { data: goals, error: goalsError } = await supabase
        .from('goals')
        .select('id, title')
        .eq('user_id', user.id)
        .eq('archived', false)
        .order('created_at', { ascending: true });

      if (goalsError) {
        console.error('Error fetching goals:', goalsError);
        setLoading(false);
        return;
      }

      const activeGoals = goals || [];

      // Fetch active (non-archived) habits
      let activeHabits: Habit[] = [];
      if (activeGoals.length > 0) {
        const goalIds = activeGoals.map((g) => g.id);
        const goalTitleMap = Object.fromEntries(
          activeGoals.map((g) => [g.id, g.title])
        );

        const { data: habits, error: habitsError } = await supabase
          .from('habits')
          .select('id, title, goal_id')
          .in('goal_id', goalIds)
          .eq('archived', false)
          .order('created_at', { ascending: true });

        if (habitsError) {
          console.error('Error fetching habits:', habitsError);
          setLoading(false);
          return;
        }

        activeHabits = (habits || []).map((h) => ({
          id: h.id,
          title: h.title,
          goalTitle: goalTitleMap[h.goal_id],
        }));
      }

      // Check if user has excess items
      const hasExcessGoals = activeGoals.length > limits.max_goals;
      const hasExcessHabits = activeHabits.length > limits.max_habits;

      if (hasExcessGoals || hasExcessHabits) {
        setAllGoals(activeGoals);
        setAllHabits(activeHabits);
        setNeedsDowngradeSelection(true);
      } else {
        setNeedsDowngradeSelection(false);
      }
    } catch (error) {
      console.error('Error checking downgrade status:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id, profile]);

  useEffect(() => {
    checkDowngrade();
  }, [checkDowngrade]);

  return {
    needsDowngradeSelection,
    allGoals,
    allHabits,
    loading,
    refreshCheck: checkDowngrade,
  };
}
